import React, { useEffect, useState } from 'react';
import Meta from 'src/components/Meta';
import { useGaEventPageView } from 'src/utils/gaEvent';
import styled from 'styled-components';
import {
  DataTitle,
  DataValue,
  NoData,
  PageCard,
  PageContainer,
  PageFrameworkLibSkeleton,
  Poppins,
  Spacer,
  TitleWithBack,
} from 'src/common';
import ExpandCollapse from 'src/common/ExpandCollapse';
import { useParams } from 'react-router';
import GenericTable, { TableSheetControlLibrary, useControlLibraryColumns } from 'src/components/GenericTable';
import useActionsApp from 'src/redux/actionHooks/useActionsApp';
import Button from 'src/components/form/Button';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { TableIds } from 'src/api/types/misc';
import { useControlFrameworkDetailsQuery } from 'src/graphql/_generated';
import _ from 'lodash';
import { isQueryLoading } from 'src/graphql';
import { showSuccessToast, toastMessages } from 'src/components/Toast';

const Div = styled(PageContainer)`
  .content {
    display: grid;
    grid-gap: 20px;

    &__row {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr;
      grid-gap: 10px;
      align-items: center;
    }
  }
  .btn-container {
    display: flex;
    justify-content: flex-end;
  }
  ${({ theme }) => theme.breakpoints.down('md')} {
    .content {
      grid-gap: 10px;
      &__row {
        grid-template-columns: 1fr;
      }
    }
  }
`;

const FrameworkLibrabryPage = () => {
  const { frameworklibraryId } = useParams<{ frameworklibraryId: string }>();
  const [isNewSelection, setIsNewSelection] = useState(true);
  const { setLocationPaths } = useActionsApp();
  useGaEventPageView('Cydea | Framework');

  const { data, loading, networkStatus } = useControlFrameworkDetailsQuery({
    variables: {
      id: frameworklibraryId,
    },
  });
  const isLoading = isQueryLoading({ loading, data, networkStatus });
  const frameworkData = data?.controlFrameworks[0];
  const controlLibs = _.orderBy(data?.controlFrameworks[0]?.frameworkControls, ['control_identifier']);

  useEffect(() => {
    if (frameworkData) {
      setLocationPaths({
        framework: { id: frameworkData.id, name: frameworkData.shortname || frameworkData.name },
      });
    }
    return () => {
      setLocationPaths({ framework: undefined });
    };
  }, [frameworkData]);

  const { columns, GTColumnSelectAnchorExported, checked, setChecked, addToRmp, isAddLoading } = useControlLibraryColumns({
    data: controlLibs,
    isCheckerEnabled: true,
    tdm: {
      onAddToRMPClick: true,
    },
    opensSameTab: true,
    tableId: TableIds.controlLibFramework,
  });

  useEffect(() => {
    if (checked.length) {
      setIsNewSelection(true);
    }
  }, [checked]);

  return (
    <>
      <Meta title={`Cydea | Framework`} feedbackScript />
      {!isLoading ? (
        <>
          {frameworkData && (
            <Div data-cy="framework-library-page">
              <PageCard>
                <TitleWithBack title="FRAMEWORK" gradient />
                <Spacer $px={30} />
                {frameworkData.shortname ? (
                  <Poppins px={32} weight={600}>
                    {frameworkData.shortname}
                  </Poppins>
                ) : (
                  <NoData />
                )}
                <Spacer $px={44} />

                <div className="content">
                  <div className="content__row">
                    <div>
                      <DataTitle>FULL NAME</DataTitle>
                      {frameworkData.name ? (
                        <DataValue data-cy="scenario-source">{frameworkData.name}</DataValue>
                      ) : (
                        <NoData />
                      )}
                    </div>
                    <div>
                      <DataTitle>AUTHOR</DataTitle>
                      {frameworkData.author ? (
                        <DataValue data-cy="scenario-event">{frameworkData.author}</DataValue>
                      ) : (
                        <NoData />
                      )}
                    </div>
                  </div>
                  <div>
                    <DataTitle>DESCRIPTION</DataTitle>
                    {frameworkData.description ? <ExpandCollapse text={frameworkData.description} /> : <NoData />}
                  </div>
                </div>
              </PageCard>
              <Spacer $px={30} />
              <div className="btn-container">
                <Button
                  primary
                  disabled={!checked.length || isAddLoading}
                  loading={isAddLoading}
                  onClick={() => {
                    addToRmp(checked).then(() => {
                      setChecked([]);
                      setIsNewSelection(false);
                      showSuccessToast(toastMessages.control.addedToRmp);
                    });
                    mpEvent(MPEvents.ButtonClick, {
                      button: 'Add to RMP',
                      tags: ['RMP'],
                    });
                  }}
                  $constWidth={200}
                  data-cy="add-to-rmp"
                >
                  {isNewSelection ? '+ ADD TO RMP' : 'ADDED TO RMP'}
                </Button>
              </div>
              <Spacer $px={30} />
              <GenericTable
                tableId={TableIds.controlLibFramework}
                data={controlLibs}
                columns={columns}
                GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
                expandContent={(row) => <TableSheetControlLibrary data={row} />}
                searchable
              />
            </Div>
          )}
        </>
      ) : (
        <Div>
          <PageFrameworkLibSkeleton />
        </Div>
      )}
    </>
  );
};

export default FrameworkLibrabryPage;
